import { type RefObject, useEffect, useRef, useState } from 'react';

// A clock for labels like "5 minutes ago": ticks while the page is open, so rendering stays pure.
export function useNow(every: number): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), every);

        return () => clearInterval(timer);
    }, [every]);

    return now;
}

// A menu or a form that closes when the pointer goes down anywhere else.
export function useOutside<T extends HTMLElement = HTMLDivElement>(open: boolean, close: () => void): RefObject<T | null> {
    const root = useRef<T>(null);
    const latest = useRef(close);
    useEffect(() => {
        latest.current = close;
    });
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDown = (event: MouseEvent): void => {
            if (!root.current?.contains(event.target as Node)) {
                latest.current();
            }
        };
        window.addEventListener('mousedown', onDown);

        return () => window.removeEventListener('mousedown', onDown);
    }, [open]);

    return root;
}

// A value that settles: the import's preview asks the server only once typing pauses.
export function useDebounced<T>(value: T, ms: number): T {
    const [settled, setSettled] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), ms);

        return () => clearTimeout(timer);
    }, [value, ms]);

    return settled;
}

// Whether a keyboard event belongs to a field being typed in, or to the page.
export const typing = (event: KeyboardEvent): boolean => {
    const target = event.target as HTMLElement | null;

    return Boolean(target && (['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable));
};
